import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Header, Footer } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";
import GoogleSignInButton from "@/components/GoogleSignInButton";
import { toast, Toaster } from "sonner";
import { Loader2, UserPlus, Eye, EyeOff } from "lucide-react";

export default function Registro() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    nombre_completo: "",
    email: "",
    password: "",
    role: "paciente",
  });
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target?.value ?? e }));

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const { data } = await api.post("/auth/registro", form);
      toast.success("Cuenta creada. Verifica tu correo con el código.");
      if (data?.email_error && !data?.email_delivered) {
        toast.warning(
          "Resend rechazó la entrega (restricción de free-tier). Usa el hint de TESTING_MODE."
        );
      }
      navigate(
        `/verificar-correo?email=${encodeURIComponent(form.email)}&purpose=register`,
        { replace: false }
      );
    } catch (err) {
      const msg = formatApiError(err);
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Header />
      <main className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          <div className="text-center mb-6">
            <div className="inline-flex items-center gap-2 rounded-full border border-border bg-white px-3 py-1 text-[11px] font-medium text-[#0F2942]">
              <UserPlus size={12} /> Crear cuenta
            </div>
            <h1 className="mt-3 font-display text-3xl font-semibold text-[#0F2942] tracking-tight">
              Regístrate en Siempre Salud
            </h1>
            <p className="text-sm text-muted-foreground mt-2">
              Selecciona tu rol y verifica tu correo con un código de 6 dígitos.
            </p>
          </div>

          <form
            onSubmit={submit}
            className="rounded-xl border border-border bg-white p-6 sm:p-7 space-y-4"
            data-testid="registro-form"
          >
            <GoogleSignInButton testid="registro-google-btn" />

            <div className="flex items-center gap-3">
              <div className="h-px bg-border flex-1" />
              <span className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">
                O regístrate con correo
              </span>
              <div className="h-px bg-border flex-1" />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="nombre">Nombre completo</Label>
              <Input
                id="nombre"
                data-testid="registro-nombre"
                value={form.nombre_completo}
                onChange={set("nombre_completo")}
                placeholder="Ej. María López García"
                required
                minLength={2}
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="email">Correo electrónico</Label>
              <Input
                id="email"
                type="email"
                data-testid="registro-email"
                value={form.email}
                onChange={set("email")}
                placeholder="tu@correo.mx"
                required
                autoComplete="email"
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="password">Contraseña</Label>
              <div className="relative">
                <Input
                  id="password"
                  data-testid="registro-password"
                  type={showPw ? "text" : "password"}
                  value={form.password}
                  onChange={set("password")}
                  placeholder="Mínimo 8 caracteres"
                  required
                  minLength={8}
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPw((v) => !v)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground p-1"
                  aria-label={showPw ? "Ocultar contraseña" : "Mostrar contraseña"}
                  data-testid="registro-password-toggle"
                >
                  {showPw ? <EyeOff size={14} /> : <Eye size={14} />}
                </button>
              </div>
            </div>

            <div className="space-y-1.5">
              <Label>Soy</Label>
              <Select
                value={form.role}
                onValueChange={(v) => setForm((f) => ({ ...f, role: v }))}
              >
                <SelectTrigger data-testid="registro-role-trigger">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="paciente" data-testid="registro-role-paciente">
                    Persona usuaria
                  </SelectItem>
                  <SelectItem value="medico" data-testid="registro-role-medico">
                    Especialista
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            {error && (
              <div
                className="rounded-md border border-destructive/30 bg-destructive/5 text-destructive text-sm px-3 py-2"
                data-testid="registro-error"
              >
                {error}
              </div>
            )}

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-[#2563EB] hover:bg-[#1e4fd0] text-white h-11"
              data-testid="registro-submit"
            >
              {loading ? (
                <>
                  <Loader2 size={14} className="animate-spin" /> Creando cuenta…
                </>
              ) : (
                "Crear cuenta"
              )}
            </Button>

            <p className="text-xs text-muted-foreground text-center pt-1">
              ¿Ya tienes cuenta?{" "}
              <Link to="/login" className="text-[#2563EB] font-medium hover:underline">
                Inicia sesión
              </Link>
            </p>
          </form>

          <p className="text-[11px] text-muted-foreground text-center mt-4 max-w-sm mx-auto leading-relaxed" data-testid="registro-legal-notice">
            Al crear tu cuenta aceptas los{" "}
            <Link
              to="/terminos-y-condiciones"
              className="text-[#2563EB] font-medium hover:underline"
              data-testid="registro-terminos-link"
            >
              Términos y Condiciones
            </Link>{" "}
            y el{" "}
            <Link
              to="/aviso-de-privacidad"
              className="text-[#2563EB] font-medium hover:underline"
              data-testid="registro-aviso-link"
            >
              Aviso de Privacidad
            </Link>{" "}
            de Siempre Salud, conforme a LFPDPPP y NOM-004-SSA3-2012.
          </p>
        </div>
      </main>
      <Footer />
      <Toaster position="top-right" richColors />
    </div>
  );
}
